/**
 * Where a context reference points on the web.
 *
 * A reference is stored as `(kind, id)` — `github-pr` + `runloopai/reflex#42`
 * — and nothing else, because the id is canonical enough to rebuild the URL
 * from (see `prUrlFromExternalId` in `@reflex/shared`). The chat used to show
 * the raw PR link an author pasted, so it was clickable; collapsing it into a
 * reference chip dropped the link, which is what this restores.
 *
 * Lives beside {@link ./ContextRefBadge} and shares its layering constraint:
 * the badge renders below the plugin registry, so the first-party kinds whose
 * URL is derivable are enumerated here. A kind whose id is an opaque
 * provider-internal key (Linear, Jira, Canny, Sentry) resolves to `null`
 * rather than a guessed URL — the same honesty rule the git-ref chips follow.
 */
import { parsePrExternalId, prUrlFromExternalId } from '@reflex/shared';
import { repoUrl } from '../../lib/repo-slug';

export interface ContextRefTarget {
  /** Canonical external URL for the referenced item. */
  url: string;
  /**
   * True when a plugin-registered `url` content scanner is expected to render
   * this reference better than the badge can — today only the pull request,
   * whose chip carries a live state and a title/branch hover card the badge
   * has no way to fetch. Kinds without a richer renderer keep the badge and
   * simply become links.
   */
  scan: boolean;
}

/** A bare `owner/repo` slug: exactly two non-empty, slash-free segments. */
const REPO_SLUG = /^[^\s/]+\/[^\s/]+$/;

/** Split an `owner/repo#<rest>` id; `null` when it isn't that shape. */
function splitRepoRef(id: string): { repoSlug: string; rest: string } | null {
  const hash = id.indexOf('#');
  if (hash <= 0 || hash === id.length - 1) return null;
  const repoSlug = id.slice(0, hash);
  if (!REPO_SLUG.test(repoSlug)) return null;
  return { repoSlug, rest: id.slice(hash + 1) };
}

/** Percent-encode a git ref for a URL path, keeping its `/` separators. */
function encodeRefPath(ref: string): string {
  return ref.split('/').map(encodeURIComponent).join('/');
}

/**
 * The web target for a context reference, or `null` when the kind's id does
 * not name a public URL (an unknown kind, an opaque provider id, or an id
 * whose shape doesn't parse — a malformed marker must not become a link to a
 * repository that happens to share its prefix).
 */
export function contextRefTarget(kind: string, id: string | undefined): ContextRefTarget | null {
  if (!id) return null;
  switch (kind) {
    case 'github-pr': {
      const url = prUrlFromExternalId(id);
      return url ? { url, scan: true } : null;
    }
    case 'github-issue': {
      // Same `owner/repo#<number>` shape as a PR id, different page.
      const parsed = parsePrExternalId(id);
      return parsed
        ? { url: `${repoUrl(parsed.repoSlug)}/issues/${parsed.number}`, scan: false }
        : null;
    }
    case 'github-repo':
      // The id is the `owner/repo` full name the picker inserted.
      return REPO_SLUG.test(id) ? { url: repoUrl(id), scan: false } : null;
    case 'github-branch': {
      // `owner/repo#<branch>`; branch names carry `/` of their own.
      const ref = splitRepoRef(id);
      return ref
        ? { url: `${repoUrl(ref.repoSlug)}/tree/${encodeRefPath(ref.rest)}`, scan: false }
        : null;
    }
    default:
      return null;
  }
}
